<template>
  <div ref="root">
    <span v-if="loading" :class="$style.loaderContainer">
      <content-loader :height="50" :width="250" :speed="2" primary-color="var(--gray-30)" secondary-color="#F2F4F8">
        <rect x="0" y="2" ry="5" width="230" height="14" rx="5" />
        <rect x="0" y="27" ry="5" width="70.56" height="12" rx="5" />
      </content-loader>
    </span>
    <a
      v-else
      target="_blank"
      rel="noreferrer noopener"
      data-testid="pin-ref-link"
      :href="previewLink"
      :class="$style.container"
      @click.stop="clicked"
    >
      <bar-chart2-icon
        v-if="preview.dashboard || preview.csatDashboard || preview.disputesDashboard"
        :class="$style.dashboardIcon"
      />
      <message-square-icon v-else-if="preview.conversation" :class="$style.conversationsIcon" />
      <pin-icon v-else-if="preview.manual" :class="$style.conversationsIcon" />
      <div data-testid="pinned-reference-preview" :class="$style.content">
        <div
          v-sanitize="title"
          data-testid="pin-title"
          class="text-sm"
          :class="deleted ? $style.deletedTitle : $style.title"
        ></div>
        <div v-if="preview.conversation || preview.manual || createdBy" :class="$style.secondRow">
          <div>
            <support-desk-icon v-if="preview.conversation" :source="preview.conversation.sourceType" />
            <span
              v-if="(preview.conversation || preview.manual) && date"
              class="text-gray-60 text-xs font-medium"
              :class="$style.date"
              >{{ date }}</span
            >
          </div>
          <div v-if="createdBy" class="text-xs font-medium text-gray-50">
            {{ $t('coaching.sessions.thread.actions.created_by', [createdBy]) }}
          </div>
        </div>
      </div>
    </a>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch, onBeforeUnmount } from 'vue'
import { ContentLoader } from 'vue-content-loader'
import { useIntersectionObserver } from '@vueuse/core'
import { BarChart2Icon, MessageSquareIcon } from '@klausapp/ui-kit/icons'
import { PinReference_Type, type PinReferenceResponse } from '@zendesk/zqa-services/pins'

import { formatDateIntl } from '@/utils/date'
import { sanitizeTicketSubject, strip } from '@/utils/sanitize'
import SupportDeskIcon from '@/modules/shared/SupportDeskIcon/SupportDeskIcon.vue'
import PinIcon from '@/assets/pin-icon.svg'
import { getTimePeriodOptions } from '@/modules/dashboard/utils/period-options'
import analytics from '@/utils/analytics'
import { bus } from '@/utils/bus'
import { getErrorResponseText } from '@/api'

import i18n from '@/i18n'
import { getPinReferencePreview } from '../api'
import {
  getDashboardPreviewLink,
  getConversationPreviewLink,
  getCsatDashboardPreviewLink,
  getDisputesDashboardPreviewLink,
} from '../utils'

const dataCardIdToTitle: Record<string, string | undefined> = {
  // Dashboard
  'dashboard-other-reasons-for-category-card': 'dashboard.card_labels.other_reasons',
  'dashboard-reasons-for-category-card': 'dashboard.card_labels.root_causes',
  'dashboard-reasons-for-category-card-all': 'dashboard.card_labels.root_causes_all',
  'dashboard-quality-scores-card': 'dashboard.card_labels.quality_scores',
  'dashboard-ratings-by-category-card': 'dashboard.card_labels.ratings_by_category',
  'dashboard-scores-by-category-card': 'dashboard.card_labels.scores_by_category',
  'dashboard-scores-by-ticket-card': 'dashboard.card_labels.scores_by_ticket',
  'dashboard-internal-quality-score-graph': 'dashboard.card_labels.internal_score',
  'dashboard-internal-quality-score-columned-graph': 'dashboard.card_labels.internal_score',
  'dashboard-workspaces-card': 'dashboard.card_labels.workspaces',
  'dashboard-users-card': 'dashboard.card_labels.users',
  // CSAT dashboard
  'csat-donut': 'csat_dashboard.card_labels.donut',
  'csat-survey-funnel': 'csat_dashboard.card_labels.funnel',
  'csat-timeline-card': 'csat_dashboard.card_labels.timeline',
  'csat-tags-card': 'csat_dashboard.card_labels.tags',
  'csat-word-cloud-card': 'csat_dashboard.card_labels.word_cloud',
  'csat-by-users-card': 'csat_dashboard.card_labels.csat_by_users',
  'csat-survey-replies-card': 'csat_dashboard.card_labels.survey_replies',
  // Disputes dashboard
  'disputes-over-time-card': 'disputes_dashboard.card.disputes_over_time',
  'disputes-by-category-card': 'disputes_dashboard.card.disputes_by_category',
  'disputers-card': 'disputes_dashboard.card.disputers',
  'disputed-reviewers-card': 'disputes_dashboard.card.disputed_reviewers',
}

const typeToResponseType: Record<PinReference_Type, keyof PinReferenceResponse> = {
  [PinReference_Type.CONVERSATION]: 'conversation',
  [PinReference_Type.DASHBOARD]: 'dashboard',
  [PinReference_Type.MANUAL]: 'manual',
  [PinReference_Type.CSAT_DASHBOARD]: 'csatDashboard',
  [PinReference_Type.DISPUTES_DASHBOARD]: 'disputesDashboard',
}

export default defineComponent({
  name: 'PinReferencePreview',
  components: { ContentLoader, BarChart2Icon, MessageSquareIcon, SupportDeskIcon, PinIcon },
  props: {
    referenceType: {
      type: String as PropType<PinReference_Type>,
      required: true,
    },
    referenceValue: {
      type: String as PropType<string>,
    },
    referenceLink: {
      type: String as PropType<string>,
      required: false,
    },
    pinId: {
      type: String as PropType<string>,
      required: false,
    },
    createdBy: String,
  },
  setup(props) {
    const root = ref<HTMLDivElement>()
    const loading = ref(false)
    const deleted = ref(false)
    const noAccess = ref(false)
    const preview = ref<PinReferenceResponse>({
      conversation: undefined,
      dashboard: undefined,
      manual: undefined,
      csatDashboard: undefined,
      disputesDashboard: undefined,
    })

    const elementVisible = ref(false)
    const { stop } = useIntersectionObserver(root, ([{ isIntersecting }]) => {
      elementVisible.value = isIntersecting
    })

    const dashboardTitle = (fallbackTitle: string, workspaceName: string) => {
      const query = new URLSearchParams(props.referenceValue)
      const sections = []

      const cardId = query.get('cardId') || ''
      const cardLabel = dataCardIdToTitle[cardId]
      if (cardLabel) {
        sections.push(i18n.t(cardLabel))
      } else if (fallbackTitle) {
        sections.push(fallbackTitle)
      }

      const timePeriod = query.get('originalTimePeriod') || query.get('timePeriod')
      const timePeriodLabel = getTimePeriodOptions().find((t) => t.value === timePeriod)?.name
      sections.push(timePeriodLabel)

      sections.push(strip(workspaceName))
      return sections.join(' • ')
    }

    const assignPreviewData = async () => {
      loading.value = true

      try {
        preview.value = await getPinReferencePreview({
          type: props.referenceType,
          value: props.referenceValue ? encodeURIComponent(props.referenceValue) : props.pinId,
        })
      } catch (err: any) {
        if (err.response.status === 403) {
          noAccess.value = true
        } else if (err.response.status === 404) {
          deleted.value = true
        } else {
          const error = await getErrorResponseText(err)
          bus.$emit('universal-error', { error })
        }
      } finally {
        loading.value = false
      }
    }

    watch(
      () => props.referenceValue,
      () => {
        if (elementVisible.value) {
          assignPreviewData()
        }
      },
    )

    watch(elementVisible, (visible) => {
      const responseType = typeToResponseType[props.referenceType]
      if (visible && !loading.value && !preview.value[responseType]) {
        assignPreviewData()
      }
    })

    onBeforeUnmount(() => stop())

    return {
      root,
      loading,
      deleted,
      noAccess,
      preview,
      // computed
      title: computed(() => {
        if (deleted.value) return i18n.t('pins.modal.conversation_deleted')
        if (noAccess.value) return i18n.t('pins.modal.no_access')
        if (preview.value.dashboard) return dashboardTitle('', preview.value.dashboard.workspaceName)
        if (preview.value.csatDashboard) {
          return dashboardTitle(i18n.t('pins.modal.csat_dashboard_title'), preview.value.csatDashboard.workspaceName)
        }
        if (preview.value.disputesDashboard) {
          return dashboardTitle(
            i18n.t('disputes_dashboard.data_column_label.disputes'),
            preview.value.disputesDashboard.workspaceName,
          )
        }

        if (preview.value.conversation) {
          return sanitizeTicketSubject(preview.value.conversation.title || i18n.t('conversations.ticket.no_subject'))
        }

        if (preview.value.manual) {
          const body = preview.value.manual.body
          return body ? sanitizeTicketSubject(body) : ''
        }

        return ''
      }),
      date: computed(() => {
        if (preview.value.conversation)
          return formatDateIntl(preview.value.conversation.createdAt as string, { month: 'short', day: 'numeric' })
        if (preview.value.manual)
          return formatDateIntl(preview.value.manual.createdAt as string, { month: 'short', day: 'numeric' })
        return ''
      }),
      previewLink: computed(() => {
        if (props.referenceLink) return props.referenceLink
        if (preview.value.dashboard) return getDashboardPreviewLink(props.referenceValue)
        if (preview.value.csatDashboard) return getCsatDashboardPreviewLink(props.referenceValue)
        if (preview.value.conversation) return getConversationPreviewLink(props.referenceValue)
        if (preview.value.disputesDashboard) return getDisputesDashboardPreviewLink(props.referenceValue)

        return ''
      }),
      // methods
      assignPreviewData,
      clicked() {
        analytics.pinReferencePreviewClicked(props.referenceType)
      },
    }
  },
})
</script>

<style lang="scss" module>
.container {
  --height: 68px;

  display: inline-flex;
  padding: 10px 16px;
  background-color: var(--white);
  box-shadow: theme('boxShadow.sm');
  border-radius: 8px;
  min-height: var(--height);
  width: 100%;

  &:hover {
    color: var(--gray-80);
    box-shadow: theme('boxShadow.sm-hover');
  }
}

.loaderContainer {
  composes: container;
  height: var(--height);
}

.dashboardIcon {
  width: 36px;
  height: 36px;
  background: var(--gray-20);
  color: var(--gray-50);
  border-radius: 50%;
  padding: 10px;
  position: relative;
  top: 6px;
  left: -1px;
  flex-shrink: 0;
  margin-right: 12px;
}

.conversationsIcon {
  composes: dashboardIcon;
  border-radius: 8px;
  top: 5px;
  left: -1px;
}

.content {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex-grow: 1;
}

.title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  text-align: left;

  color: var(--gray-80);
  margin-bottom: 4px;
}

.deletedTitle {
  composes: title;
  color: var(--gray-60);
}

.secondRow {
  display: flex;
  line-height: theme('lineHeight.tight');
  width: 100%;
  justify-content: space-between;
  align-items: center;

  > div {
    display: flex;
  }
}

.date {
  &:not(:only-child) {
    margin-left: 8px;
  }
}
</style>
